import { useTheme } from "@/src/styles/theme-provider";
import { useState } from "react";
import { useTranslation } from "react-i18next";
import { ScrollView, Text, TouchableOpacity } from "react-native";

type CategoryTagsProps = Readonly<{
  categories?: string[];
  onCategoryPress?: (category: string) => void;
}>;

const CATEGORY_BG_COLORS = [
  "--color-blush-default",
  "--color-lavender-default",
  "--color-wheat-default",
  "--color-coral-default",
  "--color-sage-default",
] as const;

export default function CategoryTags({
  categories = [],
  onCategoryPress,
}: CategoryTagsProps) {
  const [selectedCategory, setSelectedCategory] = useState<string>("__all__");
  const { t } = useTranslation();
  const { themeVars } = useTheme();

  const getCategoryBg = (index: number) =>
    themeVars[
      CATEGORY_BG_COLORS[index % CATEGORY_BG_COLORS.length]
    ] as string;

  const handlePress = (category: string) => {
    setSelectedCategory(category);
    onCategoryPress?.(category);
  };

  const isAllSelected = selectedCategory === "__all__";

  return (
    <ScrollView
      horizontal
      showsHorizontalScrollIndicator={false}
      contentContainerStyle={{ gap: 8 }}
      style={{ flexGrow: 0 }}
    >
      <TouchableOpacity
        onPress={() => handlePress("__all__")}
        activeOpacity={0.7}
        className={`items-center justify-center px-4 py-2 rounded-2xl ${
          isAllSelected
            ? "border-2 border-primary hover:bg-primary-2"
            : "border border-ash-3 hover:bg-ash-3"
        }`}
        style={{
          minWidth: 80,
          backgroundColor: themeVars["--color-chalk-default"] as string,
        }}
      >
        <Text
          className={`text-center font-semibold ${
            isAllSelected ? "text-primary" : "text-ash-1"
          }`}
        >
          {t("LABEL_ALL")}
        </Text>
      </TouchableOpacity>

      {categories.map((category, index) => {
        const isSelected = selectedCategory === category;

        return (
          <TouchableOpacity
            key={`${category}-${index}`}
            onPress={() => handlePress(category)}
            activeOpacity={0.7}
            className={`items-center justify-center px-4 py-2 rounded-2xl ${
              isSelected
                ? "border-2 border-primary hover:bg-primary-2"
                : "border border-ash-3 hover:bg-ash-3"
            }`}
            style={{
              minWidth: 80,
              backgroundColor: isSelected
                ? (themeVars["--color-chalk-default"] as string)
                : getCategoryBg(index),
            }}
          >
            <Text
              className={`text-center font-semibold ${
                isSelected ? "text-primary" : "text-ash-1"
              }`}
            >
              {category}
            </Text>
          </TouchableOpacity>
        );
      })}
    </ScrollView>
  );
}
